import {
  Box,
  Button,
  FormControl,
  FormControlLabel,
  FormLabel,
  Radio,
  RadioGroup,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import CircularProgress from "@mui/material/CircularProgress";
import {
  usePollWorkflowRunUntilCompleted,
  useTriggerWorkflowWithRunIdPoll,
  useWorkflowRuns,
} from "Hooks/Github";
import { useSnackbar } from "notistack";
import React, { SyntheticEvent, useState } from "react";
import { ENV_FULL_NAME } from "Types/env";

import MpsDemoScriptStepper from "../mps/Stepper";

enum ENV_TO_TENANT {
  DEVELOP = "leanspacedev",
  PROD = "leanspacedemo",
}

const C2ProDemoScript: React.FC = ({}) => {
  const repositoryName = "leanspacedemo-infrastructure";
  const workflowId = "prepare-c2-pro-demo.yml";

  const { enqueueSnackbar } = useSnackbar();
  const [environment, setEnvironment] = useState<ENV_FULL_NAME>(
    ENV_FULL_NAME.DEV
  );
  const [tenant, setTenant] = useState<ENV_TO_TENANT>(ENV_TO_TENANT.DEVELOP);

  const [isWorkflowTriggerButtonDisabled, setIsWorkflowTriggerButtonDisabled] =
    useState<boolean>(false);
  const [workflowRunId, setWorkflowRunId] = useState<number | undefined>(
    undefined
  );
  const [isWaitingForNewRun, setIsWaitingForNewRun] = useState(false);

  const { mutateAsync: triggerWorkflow } = useTriggerWorkflowWithRunIdPoll();
  const { refetch, latestRunId } = useWorkflowRuns(
    repositoryName,
    workflowId,
    isWaitingForNewRun
  );

  const runIdToUse = workflowRunId ?? latestRunId;

  const { data: run, dataUpdatedAt: lastRunFetch } =
    usePollWorkflowRunUntilCompleted(repositoryName, runIdToUse);

  const onRunLaunch = async (e: SyntheticEvent) => {
    e.preventDefault();

    const form = e.target as HTMLFormElement;
    setIsWaitingForNewRun(true);
    setIsWorkflowTriggerButtonDisabled(true);

    try {
      const newRunId = await triggerWorkflow({
        workflow_id: workflowId,
        repo: repositoryName,
        ref: environment === ENV_FULL_NAME.PROD ? "main" : "dev",
        inputs: {
          tenant: tenant,
          "should-clear-release-queue": form.shouldClearReleaseQueue.value,
          "delay-for-contact": form.delayForContact.value,
          "duration-of-contact": form.durationOfContact.value,
        },
      });

      setWorkflowRunId(newRunId);
      setIsWaitingForNewRun(false);
      setIsWorkflowTriggerButtonDisabled(false);

      enqueueSnackbar(
        "C2Pro demo preparation script has been requested, its state will soon be displayed in the progress bar",
        { variant: "success" }
      );
    } catch (err) {
      setIsWaitingForNewRun(false);
      setIsWorkflowTriggerButtonDisabled(false);
      enqueueSnackbar(`Failed to trigger workflow: ${err}`, {
        variant: "error",
      });
    }
  };

  const onWorkflowRefetch = () => {
    refetch();
  };

  return (
    <>
      <Box
        sx={{
          padding: 2,
        }}
      >
        <Typography sx={{ padding: 2 }} variant="h5">
          C2 Pro Demo Preparation Script
        </Typography>
        <Typography sx={{ padding: 2 }}>Demo inputs</Typography>
        <form onSubmit={onRunLaunch}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              flexWrap: "wrap",
              padding: 2,
            }}
          >
            <Tooltip title="On which environment data is generated">
              <FormControl required sx={{ paddingRight: 5 }}>
                <FormLabel id="environment">Environment</FormLabel>
                <RadioGroup
                  row
                  defaultValue={ENV_FULL_NAME.DEV}
                  name="environment"
                  onChange={(event) => {
                    setEnvironment(event.target.value as ENV_FULL_NAME);
                    setTenant(
                      event.target.value === ENV_FULL_NAME.DEV
                        ? ENV_TO_TENANT.DEVELOP
                        : ENV_TO_TENANT.PROD
                    );
                  }}
                >
                  <FormControlLabel
                    value={ENV_FULL_NAME.DEV}
                    control={<Radio />}
                    label={ENV_FULL_NAME.DEV}
                    labelPlacement="bottom"
                  />
                  <FormControlLabel
                    value={ENV_FULL_NAME.PROD}
                    control={<Radio />}
                    label={ENV_FULL_NAME.PROD}
                    labelPlacement="bottom"
                  />
                </RadioGroup>
              </FormControl>
            </Tooltip>

            <FormControl required>
              <FormLabel id="shouldClearReleaseQueue">
                Should Clear Release Queue
              </FormLabel>
              <RadioGroup
                row
                defaultValue={"false"}
                name="shouldClearReleaseQueue"
              >
                <Tooltip
                  key={"false"}
                  title={
                    <Typography variant="body2">
                      {"Doesn't clear the release queue"}
                    </Typography>
                  }
                >
                  <FormControlLabel
                    value={"false"}
                    control={<Radio />}
                    label={"false"}
                    labelPlacement="bottom"
                  />
                </Tooltip>
                <Tooltip
                  key={"true"}
                  title={
                    <Typography variant="body2">
                      {"Clears the release queue"}
                    </Typography>
                  }
                >
                  <FormControlLabel
                    value={"true"}
                    control={<Radio />}
                    label={"true"}
                    labelPlacement="bottom"
                  />
                </Tooltip>
              </RadioGroup>
            </FormControl>

            <Tooltip title="Delay before which the contact will start (default:360 seconds)">
              <TextField
                id="delayForContact"
                name="delayForContact"
                label="Delay for contact"
                sx={{ paddingRight: 5, width: "170px" }}
                type="number"
                defaultValue={360}
              />
            </Tooltip>
            <Tooltip title="Duration of the contact (default:720 seconds)">
              <TextField
                id="durationForContact"
                name="durationOfContact"
                label="Duration of contact"
                sx={{ paddingRight: 5, width: "170px" }}
                type="number"
                defaultValue={720}
              />
            </Tooltip>
          </Box>

          <Box sx={{ padding: 2, textAlign: "right" }}>
            <Button
              variant="contained"
              sx={{ marginRight: 10 }}
              onClick={onWorkflowRefetch}
            >
              Re-fetch last workflow run
            </Button>
            <Button
              disabled={isWorkflowTriggerButtonDisabled}
              variant="contained"
              type="submit"
            >
              Execute script
            </Button>
          </Box>
        </form>
      </Box>

      {isWaitingForNewRun ? (
        <Box sx={{ textAlign: "center", padding: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        run && <MpsDemoScriptStepper run={run} lastRunFetch={lastRunFetch} />
      )}
    </>
  );
};

export default C2ProDemoScript;
